import React, { ReactNode } from 'react';
import { CheckIcon, ChevronRightIcon, DotsVerticalIcon } from '@radix-ui/react-icons';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { IconButton } from '@producer/ui-kit';
import styled from 'styled-components';

import { Tooltip } from '../../tooltip/Tooltip';
import { breakPoint } from '../../../app/theme';
import {
  CheckboxItem as CheckboxMenuItem,
  OptionLabel,
  ListItem,
  SubTrigger,
} from './../../select-dropdown/content-menu/styled-content-menu';

export type Item = {
  onSelect: (event: Event) => void;
  title?: ReactNode;
  icon?: ReactNode;
  type?: 'danger' | 'warning';
  disabled?: boolean;
  hidden?: boolean;
  tooltipText?: string;
  subItems?: DropdownMenuProps['items'];
  isChecked?: never;
  onCheckedChange?: never;
  customComponent?: React.ReactElement;
};

export type CheckboxItem = Omit<Item, 'onSelect' | 'isChecked' | 'onCheckedChange'> & {
  isChecked?: boolean;
  onCheckedChange?: (isChecked: boolean) => void;
  onSelect?: (event: Event) => void;
};

type DropdownMenuProps = {
  items: Array<Item | CheckboxItem>;
  align?: 'start' | 'end';
  size?: 'small' | 'medium' | 'large' | 'free-size';
  trigger?: ReactNode;
  itemComponent?: React.ReactElement;
  tooltipText?: string;
  modal?: boolean;
  onOpenChange?: (value: boolean) => void;
  portalContainer?: HTMLDivElement;
  side?: 'bottom' | 'top';
  className?: string;
  disabled?: boolean;
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  size,
  trigger,
  itemComponent: optionComponent,
  align = 'end',
  modal = false,
  onOpenChange,
  portalContainer,
  side,
  className,
  disabled,
}) => {
  const hasIcon = items.some((item) => !!item.icon || !!item.onCheckedChange);
  const hasIconAndCheck = items.some((item) => !!item.icon && !!item.onCheckedChange);

  const renderItem = (item: Item | CheckboxItem, index: number, hasIcon?: boolean) => {
    if (item.hidden) {
      return;
    }

    if (optionComponent && React.isValidElement(optionComponent)) {
      return React.cloneElement(optionComponent, { ...item });
    }

    if (hasIconAndCheck) {
      return (
        <CheckboxMenuItem
          key={`option-${index}`}
          checked={item.isChecked}
          onCheckedChange={item.onCheckedChange}
          onSelect={item.onSelect}
        >
          <OptionLabel>
            {item.icon}

            <span>{item.title}</span>
          </OptionLabel>

          <RadixMenu.ItemIndicator>
            <CheckIcon />
          </RadixMenu.ItemIndicator>
        </CheckboxMenuItem>
      );
    }

    if (item.onCheckedChange) {
      return (
        <CheckboxMenuItem
          key={`option-${index}`}
          checked={item.isChecked}
          onCheckedChange={item.onCheckedChange}
          onSelect={item.onSelect}
        >
          <OptionLabel>
            {item.icon}

            <span>{item.title}</span>
          </OptionLabel>

          <RadixMenu.ItemIndicator>
            <CheckIcon />
          </RadixMenu.ItemIndicator>
        </CheckboxMenuItem>
      );
    }

    if (item.customComponent) {
      return React.cloneElement(item.customComponent, { ...item });
    }

    return (
      <ListItem
        key={`option-${index}`}
        disabled={item.disabled}
        onSelect={item.onSelect}
        data-type={item.type}
      >
        <OptionLabel>
          {hasIcon && item.icon}

          <span>{item.title}</span>
        </OptionLabel>
      </ListItem>
    );
  };

  return (
    <RadixMenu.Root modal={modal} onOpenChange={onOpenChange}>
      <RadixMenu.Trigger onClick={(e) => e.stopPropagation()} disabled={disabled} asChild>
        {trigger || <IconButton data-size={size} icon={<DotsVerticalIcon />} />}
      </RadixMenu.Trigger>

      <RadixMenu.Portal {...(portalContainer && { container: portalContainer })}>
        <Content side={side || 'bottom'} align={align} sideOffset={6} asChild>
          <MenuModal
            className={className || ''}
            data-size={size}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item, index) => {
              const hasSubIcon = item.subItems?.some(
                (item) => !!item.icon || !!item.onCheckedChange,
              );

              return (
                <Tooltip
                  text={item.tooltipText || 'Action not allowed'}
                  key={`tip-${index}`}
                  disabled={!item.disabled || !item.tooltipText}
                >
                  {item.subItems ? (
                    <RadixMenu.Sub key={`sub-trigger-${index}`}>
                      <SubTrigger disabled={item.disabled}>
                        {hasIcon && item.icon}

                        <span>{item.title}</span>

                        <ItemRightSlot>
                          <ChevronRightIcon />
                        </ItemRightSlot>
                      </SubTrigger>

                      <SubContent sideOffset={4}>
                        {item.subItems.map((subOption, subOptionIndex) =>
                          renderItem(subOption, subOptionIndex, hasSubIcon),
                        )}
                      </SubContent>
                    </RadixMenu.Sub>
                  ) : (
                    renderItem(item, index, hasIcon)
                  )}
                </Tooltip>
              );
            })}
          </MenuModal>
        </Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
};

const Content = styled(RadixMenu.Content)`
  @media screen and (max-width: ${breakPoint.small - 1}px) {
    max-width: 100vw;
  }
`;

const MenuModal = styled.div`
  opacity: 2;
  min-width: 18rem;
  border-radius: 0.6rem;
  padding: 0.4rem;
  z-index: calc(var(--layer-modal) + 1);
  box-shadow: rgba(0, 0, 0, 0.2) 0 4px 24px;
  backdrop-filter: blur(10px) saturate(190%) contrast(70%) brightness(80%);
  background-color: rgba(29, 30, 43, 0.498);

  &[data-size='free-size'] {
    min-width: unset;
  }
`;

const ItemRightSlot = styled.span`
  margin-left: auto;
  padding-left: 2rem;
  color: var(--color-grey-6);
`;

const SubContent = styled(RadixMenu.SubContent)`
  z-index: 9999;
  min-width: max-content;
  background: var(--color-surfaces-bg-elevation-2);
  border-radius: 0.4rem;
  padding: 0.4rem;

  @media screen and (max-width: ${breakPoint.small - 1}px) {
    &[data-side='left'] {
      transform: translate(
        calc(var(--radix-dropdown-menu-trigger-width)),
        calc(var(--radix-dropdown-menu-trigger-height) - 1rem)
      );
    }

    &[data-side='right'] {
      transform: translate(
        calc(-1 * var(--radix-dropdown-menu-trigger-width)),
        calc(var(--radix-dropdown-menu-trigger-height) - 1rem)
      );
    }
  }
`;
